import { Fetcher } from '@this/src/util';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@material-ui/core';
import { styled } from '@this/constants/themes';
import FaqItemForAIT from '@this/components/reserve_trip/dashboard/help_area_for_ait/faq_item_for_ait.template';
import { Link } from '@this/shared/ui/navigations/link';
import type { HeaderWithCodeProps } from '@this/components/reserve_trip/dashboard/help_area_for_ait/help_area_for_ait';
import HelpAccordion from '@this/components/reserve_trip/dashboard/help_area_for_ait/help_accordion';
import type { KnowledgeCategoryResponseArgs } from '@this/domain/knowledge_category';
import KnowledgeCategory, { convertKnowledgeCategoryResponseToArgs } from '@this/domain/knowledge_category';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { sizes } from '@this/components/shared/atoms/media';

type Props = {
  header: HeaderWithCodeProps;
  items: { type?: string; href: string; text: string }[];
  showSubCategory: boolean;
};

interface AccordionItemProps {
  subTitleText: string;
  items: { href: string; text: string }[];
  showSubCategory: boolean;
}

interface KnowledgeCategoriesResponse {
  knowledge_categories: KnowledgeCategoryResponseArgs[];
}

const CATEGORY_NAME_BREAK_PREFIXES = ['その他の', 'カスタム・'] as const;
const MEDIA_QUERY_OPTIONS = { noSsr: true };

const formatCategoryName = (name: string): React.ReactNode => {
  const prefix = CATEGORY_NAME_BREAK_PREFIXES.find(p => name.startsWith(p));
  if (!prefix) return name;
  return (
    <span>
      {prefix}
      <br />
      {name.slice(prefix.length)}
    </span>
  );
};

const AccordionItem: React.FC<AccordionItemProps> = ({ subTitleText, items, showSubCategory }) => (
  <HelpAccordion icon={<KnowledgeIcon />} title={subTitleText}>
    <FaqAccordionContents items={items} showSubCategory={showSubCategory} />
  </HelpAccordion>
);

const FaqAccordionContents: React.FC<Omit<AccordionItemProps, 'subTitleText'>> = ({ items, showSubCategory }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [knowledgeCategories, setKnowledgeCategories] = useState<KnowledgeCategory[]>([]);
  const isMobile = useMediaQuery(`(max-width: ${sizes.spSmall}px)`, MEDIA_QUERY_OPTIONS);
  const categoryCardStyle = useMemo(
    () =>
      ({
        display: 'inline-flex',
        flexDirection: 'column',
        gap: '12px',
        padding: isMobile ? 0 : '24px',
        width: '100%',
        aspectRatio: isMobile ? '1' : undefined,
        boxSizing: 'border-box',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px #ECECEC solid',
        cursor: 'pointer',
        color: '#3A3A3A',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: 1.4,
        wordWrap: 'break-word',
        textAlign: 'center'
      } as const),
    [isMobile]
  );
  const categoryLabelStyle = useMemo(
    () =>
      ({
        minHeight: isMobile ? '2.8em' : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center' as const,
        width: '100%'
      } as const),
    [isMobile]
  );

  const fetchKnowledgeCategories = useCallback(() => {
    setIsLoading(true);
    Fetcher.get<KnowledgeCategoriesResponse>('/knowledge_categories', { include_children: true })
      .then(res => {
        setKnowledgeCategories(
          res.knowledge_categories.map(raw => new KnowledgeCategory(convertKnowledgeCategoryResponseToArgs(raw)))
        );
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchKnowledgeCategories();
  }, [fetchKnowledgeCategories]);

  return (
    <AccordionContentsItems>
      <FaqSubTitle>閲覧の多いご質問</FaqSubTitle>
      <ul
        style={{
          listStyleType: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexWrap: 'wrap'
        }}
      >
        {items.map(item => (
          <FaqItemForAIT key={item.href} href={item.href} text={item.text} />
        ))}
      </ul>
      <div
        style={{
          padding: isMobile ? '12px 0' : '24px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '12px' : '24px'
        }}
      >
        <FaqSubTitle>カテゴリから探す</FaqSubTitle>
        {isLoading ? (
          <Loading />
        ) : (
          <Categories>
            {knowledgeCategories.length > 0 &&
              knowledgeCategories.map(category => (
                <div key={category.id}>
                  <Link
                    href={`/knowledge_categories/${category.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      width: '100%'
                    }}
                  >
                    <div style={categoryCardStyle}>
                      <img
                        src={`/images/help/category_${category.id}.svg`}
                        alt={`category_${category.id}`}
                        width={40}
                        height={40}
                        style={{ display: 'block', width: 40, height: 40, flexShrink: 0 }}
                      />
                      <span style={categoryLabelStyle}>
                        {isMobile ? formatCategoryName(category.sfName) : category.sfName}
                      </span>
                    </div>
                  </Link>
                  {showSubCategory && (
                    <SubCategories>
                      {category.children.length > 0 &&
                        category.children.map(
                          subCat =>
                            subCat.knowledgesCount > 0 && (
                              <li key={subCat.id} style={{ padding: '6px 16px 6px 0' }}>
                                <SubCategoryLink
                                  href={`/knowledge_categories/${category.id}#${subCat.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {subCat.sfName} ({subCat.knowledgesCount})
                                </SubCategoryLink>
                              </li>
                            )
                        )}
                    </SubCategories>
                  )}
                </div>
              ))}
          </Categories>
        )}
      </div>
    </AccordionContentsItems>
  );
};

const FaqBlockForAIT: React.FC<Props> = ({ header, items, showSubCategory }) => {
  return (
    <Wrapper>
      <Header style={header.mainTitle ? undefined : { marginBottom: 0 }}>
        {header.mainTitle ? <MainTitle>{header.mainTitle}</MainTitle> : null}
        {header.subTitles.map((subTitle, index) => (
          <AccordionItem
            key={index}
            subTitleText={subTitle.text}
            items={items}
            showSubCategory={showSubCategory}
          />
        ))}
      </Header>
    </Wrapper>
  );
};

const KnowledgeIcon: React.FC = () => <img src="/images/help/knowledge_icon.svg" alt="knowledge" />;

const Wrapper = styled.div`
  flex-basis: 320px;
  flex-grow: 999;
`;

const Header = styled.div`
  margin-bottom: 40px;
`;

const MainTitle = styled.div`
  color: #3a3a3a;
  font-size: 23px;
  font-weight: 700;
  line-height: 28.75px;

  @media screen and (max-width: ${sizes.spSmall}px) {
    font-size: 20px;
    line-height: 25px;
  }
`;

const AccordionContentsItems = styled.div`
  width: 100%;
  padding: 24px;
  gap: 24px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.12);
`;

const FaqSubTitle = styled.div`
  color: #3a3a3a;
  font-size: 17px;
  font-weight: 700;
  line-height: 21.25px;
  word-wrap: break-word;
`;

const Categories = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;

  > div {
    width: 100%;
    box-sizing: border-box;
  }

  @media screen and (max-width: ${sizes.spSmall}px) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: flex-start;

    > div {
      width: calc((100% - 12px) / 2);
      flex-shrink: 0;
    }
  }
`;

const SubCategories = styled.ul`
  margin-top: 24px;
  padding: 0 0 0 16px;

  @media screen and (max-width: ${sizes.spSmall}px) {
    margin-top: 12px;
  }
`;

const SubCategoryLink = styled(Link)`
  color: #3a3a3a;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  word-wrap: break-word;
`;
export default FaqBlockForAIT;
